import { useEffect, useMemo, useState } from 'react';
import {
    Sheet,
    SheetContent,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import PermissionChecklist from '../shared/PermissionChecklist';
import { RIGHTS_GROUPS, PERMISSION_ROWS } from './actorAccessItems';
import type { ActorWithCount, ActorScope } from '../../services/api/actorService';
import { getActorDisplayName } from '../../utils/roleUtils';

interface ActorFormDrawerProps {
    open: boolean;
    actor: ActorWithCount | null;
    /** manageEndUsers-only callers: no scope, no rights */
    limited?: boolean;
    onOpenChange: (open: boolean) => void;
    onSave: (data: {
        role: string;
        description: string;
        scope?: ActorScope;
        rights: string[];
        deptPermissions: string[];
        alternativeNames: Record<string, string>;
        alternativeDescriptions: Record<string, string>;
    }) => Promise<void>;
}

const RIGHTS_SECTIONS = RIGHTS_GROUPS.flatMap((group) =>
    group.subgroups.map((sub) => ({
        label: `${group.group} — ${sub.label}`,
        items: sub.rows,
    }))
);

const PERMISSION_SECTIONS = [{ items: PERMISSION_ROWS }];

const LANGUAGES = [
    { code: 'en', label: 'EN' },
    { code: 'da', label: 'DA' },
    { code: 'no', label: 'NO' },
    { code: 'fi', label: 'FI' },
    { code: 'fr', label: 'FR' },
    { code: 'pt-BR', label: 'PT-BR' },
    { code: 'pt-PT', label: 'PT-PT' },
];

export default function ActorFormDrawer({
    open,
    actor,
    limited = false,
    onOpenChange,
    onSave,
}: ActorFormDrawerProps) {
    const isEdit = !!actor;
    const [role, setRole] = useState('');
    const [description, setDescription] = useState('');
    const [scope, setScope] = useState<ActorScope | undefined>(undefined);
    const [rights, setRights] = useState<string[]>([]);
    const [deptPermissions, setDeptPermissions] = useState<string[]>([]);
    const [alternativeNames, setAlternativeNames] = useState<Record<string, string>>({});
    const [alternativeDescriptions, setAlternativeDescriptions] = useState<
        Record<string, string>
    >({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (actor) {
            setRole(actor.role);
            setDescription(actor.description ?? '');
            setScope(actor.scope);
            setRights([...actor.rights]);
            setDeptPermissions([...actor.deptPermissions]);
            setAlternativeNames({ ...(actor.alternativeNames ?? {}) });
            setAlternativeDescriptions({
                ...(actor.alternativeDescriptions ?? {}),
            });
        } else {
            setRole('');
            setDescription('');
            setScope(undefined);
            setRights([]);
            setDeptPermissions([]);
            setAlternativeNames({});
            setAlternativeDescriptions({});
        }
    }, [actor, open]);

    const canSave = useMemo(
        () => role.trim().length > 0 && !saving,
        [role, saving]
    );

    const handleSave = async () => {
        if (!canSave) return;
        setSaving(true);
        try {
            const filteredNames = Object.fromEntries(
                Object.entries(alternativeNames).filter(([, v]) => v.trim())
            );
            const filteredDescriptions = Object.fromEntries(
                Object.entries(alternativeDescriptions).filter(([, v]) =>
                    v.trim()
                )
            );
            await onSave({
                role: role.trim(),
                description: description.trim(),
                scope,
                rights,
                deptPermissions,
                alternativeNames: filteredNames,
                alternativeDescriptions: filteredDescriptions,
            });
            onOpenChange(false);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="flex w-full flex-col gap-0 p-0 sm:w-[560px] sm:max-w-[560px]"
            >
                <SheetHeader className="border-b border-subtle/60 px-6 py-4">
                    <SheetTitle>
                        {isEdit
                            ? `Edit Actor — ${getActorDisplayName(actor)}`
                            : 'Create Actor'}
                    </SheetTitle>
                </SheetHeader>

                <div className="flex-1 space-y-6 overflow-y-auto px-6 py-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="actor-role-name">Role Name</Label>
                        <Input
                            id="actor-role-name"
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            placeholder="e.g. Radiographer"
                            disabled={isEdit}
                        />
                        {isEdit && (
                            <p className="text-xs text-muted-foreground">
                                Role names are fixed. Use the alternative names
                                below to change what users see.
                            </p>
                        )}
                    </div>

                    {!limited && (
                        <div className="space-y-1.5">
                            <Label>Scope</Label>
                            <Select
                                value={scope ?? ''}
                                onValueChange={(v) => setScope(v as ActorScope)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Unknown" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="global">Global</SelectItem>
                                    <SelectItem value="department">Department</SelectItem>
                                    <SelectItem value="manufacturer">Manufacturer</SelectItem>
                                    <SelectItem value="nordinsight">NordInsight</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label>Alternative Names</Label>
                        <p className="text-xs text-muted-foreground">
                            Display names for each supported language. Falls
                            back to the role name when blank.
                        </p>
                        <div className="flex flex-col gap-2">
                            {LANGUAGES.map(({ code, label }) => (
                                <div
                                    key={code}
                                    className="flex items-center gap-2"
                                >
                                    <span className="w-12 shrink-0 text-right text-xs font-medium text-muted-foreground">
                                        {label}
                                    </span>
                                    <Input
                                        className="h-8 text-sm"
                                        placeholder={role || 'Display name'}
                                        value={alternativeNames[code] ?? ''}
                                        onChange={(e) =>
                                            setAlternativeNames((prev) => ({
                                                ...prev,
                                                [code]: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="actor-description">Description</Label>
                        <Textarea
                            id="actor-description"
                            rows={2}
                            className="min-h-0"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="What this role is for"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>Alternative Descriptions</Label>
                        <p className="text-xs text-muted-foreground">
                            Description for each supported language. Falls back
                            to the description above when blank.
                        </p>
                        <div className="flex flex-col gap-2">
                            {LANGUAGES.map(({ code, label }) => (
                                <div
                                    key={code}
                                    className="flex items-center gap-2"
                                >
                                    <span className="w-12 shrink-0 text-right text-xs font-medium text-muted-foreground">
                                        {label}
                                    </span>
                                    <Textarea
                                        rows={2}
                                        className="min-h-0 text-sm"
                                        placeholder={
                                            description || 'Description'
                                        }
                                        value={
                                            alternativeDescriptions[code] ?? ''
                                        }
                                        onChange={(e) =>
                                            setAlternativeDescriptions(
                                                (prev) => ({
                                                    ...prev,
                                                    [code]: e.target.value,
                                                })
                                            )
                                        }
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Department Permissions Template</Label>
                        <p className="text-xs text-muted-foreground">
                            The department permissions applied to users under
                            this actor.
                        </p>
                        <PermissionChecklist
                            sections={PERMISSION_SECTIONS}
                            selected={deptPermissions}
                            onChange={setDeptPermissions}
                        />
                    </div>

                    {!limited && (
                        <div className="space-y-2">
                            <Label>Rights</Label>
                            <PermissionChecklist
                                sections={RIGHTS_SECTIONS}
                                selected={rights}
                                onChange={setRights}
                            />
                        </div>
                    )}
                </div>

                <SheetFooter className="border-t border-subtle/60 px-6 py-4">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={!canSave}>
                        {saving
                            ? 'Saving…'
                            : isEdit
                            ? 'Save Changes'
                            : 'Create'}
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}
